import React, { useRef, useState } from 'react';
import { Button, Form, Spinner } from 'reactstrap';
import AuthenticatorSetup from './AuthenticatorSetup';
import CodeField from './CodeField';
import OtpCodeFields from './OtpCodeFields';
import {
  AUTH_ERROR_CODES,
  getAuthErrorCode,
  getAuthErrorMessage,
  verifyLoginMfa,
} from './supplierAuthApi';

// Langkah kedua login Registered Supplier: kode aplikasi authenticator (atau recovery code).
// Bila belum pernah enroll, QR/secret ditampilkan dulu.
const LoginMfaStep = ({ challenge, onSuccess, onCancel, onFatalError }) => {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const autoSubmittedRef = useRef('');
  const isSetup = !!challenge.mfaSetupRequired;

  const submitCode = async (rawCode, { isRecovery = useRecovery } = {}) => {
    if (submittingRef.current) return;

    const value = String(rawCode ?? '').trim();
    if (!isRecovery && value.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app.');
      return;
    }
    if (isRecovery && !value) {
      setError('Please enter one of your recovery codes.');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const data = await verifyLoginMfa({
        mfaToken: challenge.mfaToken,
        code: value,
        isRecoveryCode: isRecovery,
      });
      await onSuccess(data);
    } catch (err) {
      if (getAuthErrorCode(err) === AUTH_ERROR_CODES.invalidMfaCode) {
        setError(getAuthErrorMessage(err));
        setCode('');
        autoSubmittedRef.current = '';
      } else {
        onFatalError(err);
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleAuthenticatorCodeChange = nextCode => {
    setCode(nextCode);
    if (nextCode.length !== 6) return;
    if (submittingRef.current) return;
    if (autoSubmittedRef.current === nextCode) return;
    autoSubmittedRef.current = nextCode;
    submitCode(nextCode, { isRecovery: false });
  };

  const submit = async e => {
    e.preventDefault();
    await submitCode(code);
  };

  const toggleRecovery = () => {
    setUseRecovery(prev => !prev);
    setCode('');
    setError('');
    autoSubmittedRef.current = '';
  };

  return (
    <Form className='flex-grow-1 d-flex flex-column' onSubmit={submit}>
      {isSetup && (
        <AuthenticatorSetup
          sharedKey={challenge.sharedKey}
          authenticatorUri={challenge.authenticatorUri}
        />
      )}

      {useRecovery ? (
        <CodeField
          id='mfa-recovery-code'
          label='Recovery code'
          icon='ri-key-2-line'
          value={code}
          onChange={setCode}
          error={error}
          numeric={false}
          maxLength={20}
          placeholder='XXXXX-XXXXX'
          autoFocus
        />
      ) : (
        <OtpCodeFields
          id='mfa-code'
          label='Authentication code'
          value={code}
          onChange={handleAuthenticatorCodeChange}
          error={error}
          disabled={submitting}
          autoFocus
        />
      )}

      <Button
        type='submit'
        disabled={submitting}
        className='btn login-btn-primary w-100 d-flex align-items-center justify-content-center mb-3'
      >
        {submitting ? (
          <>
            <Spinner size='sm' className='me-2' aria-hidden='true' />
            Verifying...
          </>
        ) : (
          <>
            <i className='ri-shield-check-line me-2' aria-hidden='true' />
            {isSetup ? 'Activate & Sign In' : 'Verify'}
          </>
        )}
      </Button>

      <div className='d-flex justify-content-between flex-wrap gap-2'>
        <button
          type='button'
          className='btn btn-link p-0 mfa-link'
          onClick={onCancel}
          disabled={submitting}
        >
          <i className='ri-arrow-left-line me-2' aria-hidden='true' />
          Back to sign in
        </button>
        {!isSetup && (
          <button
            type='button'
            className='btn btn-link p-0 mfa-link'
            onClick={toggleRecovery}
            disabled={submitting}
          >
            {useRecovery
              ? 'Use authenticator app'
              : 'Lost your phone? Use a recovery code'}
          </button>
        )}
      </div>
    </Form>
  );
};

export default LoginMfaStep;
